import React, { useEffect, useMemo, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Auth } from 'aws-amplify';
import { Table } from 'react-bootstrap';
import { FaDownload, FaPencilAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AddEditManualModal from './AddEditManualModal';
import { createManualWithFile, fetchManuals, getManualUrl, updateManualRecord } from './operations';
import { MANUAL_PORTALS } from './portals';
import { SubHeader, Footer, Button, Breadcrumbs, EdgeReportingWindow } from '../../components';

import './styles.scoped.css';

// EDGE Reporting window frame — matches the sibling reporting screens' EdgeReportingWindow usage
const WINDOW_HEADER_STYLE = { backgroundColor: '#343a40' };
const WINDOW_CONTAINER_STYLE = { border: '2px solid #343a40' };

const Manuals = () => {
  const navigate = useNavigate();
  const user = useSelector(state => state.admin.user);

  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [currentUser, setCurrentUser] = useState({ email: '', fullName: '' });

  const refresh = () => {
    setLoading(true);
    fetchManuals()
      .then(setManuals)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(authUser => {
        const email = authUser?.attributes?.email ?? '';
        setCurrentUser({ email, fullName: user?.name || email });
      })
      .catch(e => console.error('Manuals: failed to read the current user', e));
  }, [user]);

  const manualByPortal = useMemo(() => {
    const map = {};
    manuals.forEach(m => {
      map[m.portal] = m;
    });
    return map;
  }, [manuals]);

  const handleDownload = async manual => {
    setDownloadError(null);
    const url = await getManualUrl(manual.documentName);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setDownloadError('The manual could not be downloaded. Please try again.');
    }
  };

  const openEditor = (portal, manual) => {
    setSaveError(null);
    setEditing({ portal, manual });
  };

  const handleSave = async data => {
    setSaving(true);
    setSaveError(null);
    const existing = editing?.manual;
    const base = {
      uploadedByEmail: currentUser.email,
      uploadedByFullName: currentUser.fullName,
      ...data,
    };
    const result = existing
      ? await updateManualRecord({ id: existing.id, currentDocumentName: existing.documentName, ...base })
      : await createManualWithFile({ portal: editing.portal.key, ...base });
    setSaving(false);
    if (result) {
      setEditing(null);
      refresh();
    } else {
      setSaveError('The manual could not be saved. Please try again.');
    }
  };

  return (
    <>
      <SubHeader
        text="Admin Portal"
        buttons={[{ icon: <Web className="subheader-icon" />, onPress: () => navigate(-1) }]}
      />

      <div className="wrap">
        <Breadcrumbs leftTitle="Admin Portal" rightTitle="Portal Manuals" route="/admin-portal" />
        <EdgeReportingWindow
          windowTitle="Manuals"
          headerStyle={WINDOW_HEADER_STYLE}
          containerStyle={WINDOW_CONTAINER_STYLE}
          windowContent={
            <Table borderless hover className="manuals-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Visible To</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {MANUAL_PORTALS.map(portal => {
                  const manual = manualByPortal[portal.key];
                  return (
                    <tr key={portal.key}>
                      <td>{manual?.title}</td>
                      <td>{manual?.fileType}</td>
                      <td>{portal.label}</td>
                      <td>
                        <div className="actions">
                          {manual && (
                            <Button
                              variant="text"
                              icon={<FaDownload />}
                              aria-label={`Download ${portal.label} manual`}
                              onPress={() => handleDownload(manual)}
                            />
                          )}
                          <Button
                            variant="text"
                            icon={<FaPencilAlt />}
                            aria-label={`Edit ${portal.label} manual`}
                            onPress={() => openEditor(portal, manual)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          }
        />
        {loading && <span className="loading">Loading…</span>}
        {downloadError && <span className="page-error">{downloadError}</span>}
      </div>

      {editing && (
        <AddEditManualModal
          manual={editing.manual}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
          saving={saving}
          saveError={saveError}
        />
      )}

      <Footer />
    </>
  );
};

export default Manuals;
